import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { GenericForm, GenericFormField } from '@/styles/GenericForm';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { useEntityComments } from '@/hooks/useEntityComments';
import { fetchEntityAttachments } from '@/lib/entity-attachments';
import { uploadSpecificationAttachments, deleteSpecificationAttachment } from '@/features/project-management/specifications/store';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { trackSpecificationCreated, trackSpecificationUpdated, trackFormSubmission } from '@/lib/posthog-events';

interface Project {
  id: string;
  name: string;
}

interface User {
  id: string;
  full_name: string;
  company_name?: string | null;
  email?: string;
}

interface Specification {
  id: string;
  title: string;
  description: string;
  project_id: string;
  assignee_id: string;
  priority: string;
}

interface SpecificationFormProps {
  mode: 'create' | 'edit';
  specification?: Specification;
  projects: Project[];
  users: User[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  /** A comment or an attachment was added/removed: the list's counters must reload. */
  onChanged?: () => void;
  isOpen: boolean;
  error?: string | null;
}

export function SpecificationForm({
  mode,
  specification,
  projects,
  users,
  onSubmit,
  onCancel,
  onChanged,
  isOpen,
  error,
}: SpecificationFormProps) {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  const specificationId = mode === 'edit' ? specification?.id : undefined;

  // Attachments: the generic system, exactly like every other module. The Generic section itself
  // asks "are you sure?" before a delete -- this form adds no second confirmation.
  const uploadSpecificationAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    const uploaded = await uploadSpecificationAttachments(id, files, companyId, user.id);
    if (uploaded.length > 0) onChanged?.();
    return uploaded;
  }, [companyId, user, onChanged]);
  const deleteSpecificationAttachmentFn = useCallback(async (id: string) => {
    const ok = await deleteSpecificationAttachment(id, user?.id);
    if (ok) onChanged?.();
    return ok;
  }, [user, onChanged]);
  const {
    pendingFiles,
    uploadedFiles: uploadedAttachments,
    setUploadedFiles: setUploadedAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteAttachment,
    resetPending: resetPendingFiles,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: specificationId,
    uploadFn: uploadSpecificationAttachmentsFn,
    deleteFn: deleteSpecificationAttachmentFn,
  });

  // Comments: the generic hook + section (3 audiences), exactly like every other module.
  const { comments, addComment, deleteComment } = useEntityComments('specification', specificationId);
  const handleAddComment = async (content: string, audience?: string) => {
    await addComment(content, audience);
    onChanged?.();
  };
  const handleDeleteComment = async (commentId: string) => {
    await deleteComment(commentId);
    onChanged?.();
  };

  // The form loads its OWN attachments when it opens (one query + one batch signing), instead of the
  // page loading them before the form could show anything.
  useEffect(() => {
    if (isOpen && specificationId) {
      fetchEntityAttachments('specification', specificationId, { ascending: false })
        .then(setUploadedAttachments)
        .catch((err) => console.error('Failed to load attachments:', err));
    } else {
      setUploadedAttachments([]);
    }
  }, [isOpen, specificationId, setUploadedAttachments]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const previousIsOpenRef = useRef(false);
  const initialValuesRef = useRef<Record<string, any>>({});

  // Initialize form data when form opens
  const initialValues = useMemo(() => {
    if (mode === 'edit' && specification) {
      return {
        project_id: specification.project_id || '',
        title: specification.title || '',
        description: specification.description || '',
        assignee_id: specification.assignee_id || '',
        priority: specification.priority || 'medium',
      };
    }
    return {
      project_id: getDefaultProjectIdForNewRecord(selectedProject),
      title: '',
      description: '',
      assignee_id: '',
      priority: 'medium',
    };
  }, [mode, specification, selectedProject]);

  // Capture initial values when form opens
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));
      // This form stays mounted between opens (isOpen just toggles a null
      // render), so a file staged before a cancelled create would otherwise
      // still be sitting in state the next time this opens.
      resetPendingFiles();
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen, initialValues, resetPendingFiles]);

  const specificationFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      row: 1,
      colSpan: 1,
      placeholder: 'Enter specification title',
    },
    {
      name: 'project_id',
      label: 'Project',
      type: 'select',
      required: true,
      row: 1,
      colSpan: 1,
      options: projects.map(p => ({ label: p.name, value: p.id })),
      placeholder: 'Select',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      row: 2,
      colSpan: 2,
      placeholder: 'Enter specification description',
    },
    {
      name: 'assignee_id',
      label: 'Assignee',
      type: 'select',
      row: 3,
      colSpan: 1,
      options: users.map(u => ({ label: u.company_name || u.full_name || u.email || '', value: u.id })),
      placeholder: 'Select',
      membershipProjectField: 'project_id',
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      row: 3,
      colSpan: 1,
      options: [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
      ],
      placeholder: 'Select',
    },
  ], [projects, users]);

  const handleSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      const assigneeId = data.assignee_id || '';
      
      const submitData = {
        title: data.title?.trim() || '',
        description: data.description?.trim() || '',
        project_id: data.project_id,
        assignee_id: assigneeId || null,
        priority: data.priority || 'medium',
        attachmentsFiles: pendingFiles, // Pass pending files for upload after creation
      };

      await onSubmit(submitData);
    } catch (err) {
      console.error('Error submitting specification form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <GenericForm
      title="Specification"
      mode={mode}
      fields={specificationFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isOpen={isOpen}
      error={error}
      submitButtonText="Save Changes"
      saving={isSubmitting}
      additionalSections={(values) => (
        <div className="space-y-6">
          {/* Attachments Section */}
          <GenericAttachmentsSection
            attachments={uploadedAttachments}
            pendingFiles={pendingFiles}
            onUpload={handleFileUpload}
            onDelete={mode === 'edit' ? handleDeleteAttachment : undefined}
            onRemovePending={handleRemovePendingFile}
            mode={mode}
            disabled={false}
            projectId={values.project_id || ''}
          />

          {/* Comments Section */}
          <GenericCommentsSection
            comments={comments}
            onAddComment={mode === 'edit' && specificationId ? handleAddComment : async () => {}}
            onDeleteComment={mode === 'edit' ? handleDeleteComment : undefined}
            mode={mode}
            audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
            placeholder={mode === 'create' ? 'Comments are available after creating the specification' : 'Add a comment...'}
          />
        </div>
      )}
    />
  );
}
